// src/blogPosts.js
// Central config for all blog articles (6 topics × 5 languages)

export const BLOG_POSTS = [
  // ---------------------------------------------------------------------------
  // ARTICLE 1 – ATS friendly CV for any country
  // ---------------------------------------------------------------------------
  {
    id: "ats-friendly-any-country",
    cluster: "ats-core",
    date: "2025-12-02",
    readTime: "7 min",
    tags: ["ATS", "CV", "Keywords"],
    categoryLabel: {
      en: "ATS-Friendly CVs",
      fr: "CV compatibles ATS",
      de: "ATS-freundliche Lebensläufe",
      it: "CV compatibili con ATS",
      es: "CV compatibles con ATS",
    },
    slug: {
      en: "ats-friendly-cv-any-country",
      fr: "cv-compatible-ats-tous-pays",
      de: "ats-freundlicher-lebenslauf-fuer-jedes-land",
      it: "cv-compatibile-ats-per-ogni-paese",
      es: "cv-compatible-ats-para-cualquier-pais",
    },
    h1: {
      en: "How To Create An ATS Friendly CV That Works In Any Country",
      fr: "Comment créer un CV compatible avec les ATS pour n’importe quel pays",
      de: "So erstellen Sie einen ATS freundlichen Lebenslauf für jedes Land",
      it: "Come creare un CV compatibile con gli ATS per qualsiasi paese",
      es: "Cómo crear un CV compatible con ATS para cualquier país",
    },
    metaTitle: {
      en: "How To Create An ATS Friendly CV For Any Country",
      fr: "Créer un CV compatible ATS pour tous les pays",
      de: "ATS freundlichen Lebenslauf für jedes Land erstellen",
      it: "CV compatibile ATS per qualsiasi paese",
      es: "Crear un CV compatible con ATS para cualquier país",
    },
    metaDescription: {
      en: "Learn how to create an ATS friendly CV that works in any country. Structure, formatting, keywords, and a free ATS scan with YourSmartCV.",
      fr: "Apprenez à créer un CV compatible ATS pour tous les pays. Structure, format, mots clés et analyse gratuite avec YourSmartCV.",
      de: "Lernen Sie, wie Sie einen ATS freundlichen Lebenslauf für jedes Land erstellen. Struktur, Formatierung, Schlüsselwörter und ein kostenloser ATS-Scan mit YourSmartCV.",
      it: "Scopri come creare un CV compatibile con gli ATS che funziona in qualsiasi paese. Struttura, formato, parole chiave e analisi gratuita con YourSmartCV.",
      es: "Aprende a crear un CV compatible con ATS para cualquier país. Estructura, formato, palabras clave y un escaneo ATS gratuito con YourSmartCV.",
    },
    body: {
      en: `
If you apply online today, your CV is usually read by an ATS, an applicant tracking system, before a person sees it.

If the ATS cannot read your CV correctly, you may never appear in recruiter search results, even if you are a strong candidate.

This guide shows you how to create an ATS friendly CV that works in any country and in any language. At the end, you will see how to test your CV with an ATS scan using YourSmartCV.

> If you want to understand the software behind all this in more detail, read our guide on [what an ATS is and how recruiters really use it](/en/blog/what-is-an-ats-and-how-recruiters-use-it/).

## 1. What an ATS does with your CV

An ATS is software that companies use to manage applications. It can:

- store thousands of CVs in one place  
- read and extract your information  
- search by skills, job titles and keywords  
- rank candidates by how well they match a job  

If your CV is not ATS friendly, the system may:

- fail to read your text  
- miss important skills  
- place you very low in search results  

Your goal is simple:  
make your CV easy for ATS software to read and easy for humans to scan.

## 2. Safe structure for an ATS friendly CV

This structure works well in most countries and industries:

- Contact details  
- Professional summary  
- Key skills  
- Experience  
- Education  
- Optional: Languages, certifications, tools  

### Contact details

Include:

- full name  
- city and country  
- email  
- phone with country code  
- optional links such as LinkedIn or portfolio  

Use **normal text** in the main body of the page. Avoid putting your contact details in headers, footers, text boxes or images. Many ATS tools ignore these areas.

### Professional summary

Write three to five lines that explain:

- your profile and years of experience  
- your main area (data, marketing, finance, product…)  
- the type of role you want  
- one or two key strengths  

Use keywords from your target job title in this summary.

### Key skills

Create a clear list, grouped by topic. For example:

- Methods: A/B testing, forecasting, MMM  
- Tools: Python, R, SQL, Power BI, Tableau  
- Business: performance marketing, pricing, customer analytics  
- Languages: English, Spanish, German  

Use the same wording that appears in job descriptions, as long as it is true for you.

### Experience

For each role include:

- job title  
- company name, city, country  
- dates in month and year  
- one line that explains the role  
- four to seven bullets with achievements  

Focus on your main area, for example data science, marketing, development, finance. The type of role you want should be clear.

Include numbers where possible:

- improved conversion by 8%  
- reduced churn by 10%  
- managed €1M annual budget  

### Education and extra sections

List degree, institution, country and dates.

Optional sections:

- Languages  
- Certifications / training  
- Tools & technologies  

Use bullet lists, not long paragraphs.

## 3. Formatting rules that keep ATS and humans happy

Keep your CV ATS safe:

- use a simple one-column layout  
- use common fonts such as Arial, Calibri or similar  
- use clear headings  
- use simple bullet points  
- avoid complex graphics, shapes and icons for key information  

Save your CV as PDF unless the employer asks for another format.

To see how well your CV works in practice, you can always test it with an ATS checker or YourSmartCV.

## 4. Use keywords from job descriptions (without stuffing)

Take a job description and highlight:

- job title  
- required skills  
- tools and platforms  
- industry terms  
- languages and certifications  

Add relevant terms naturally to:

- Summary  
- Skills section  
- Experience bullets  
- Tools / Technologies section  

Avoid copying the job ad word for word. Keep your CV about **you**.

## 5. Common problems that block ATS

Typical issues include:

- complex two-column layouts with text inside shapes  
- important details in headers, footers or images  
- missing keywords  
- very long CVs with outdated details  

For specific examples, read about the [ten common CV mistakes that block ATS and recruiters](/en/blog/common-cv-mistakes-that-block-ats-and-recruiters/).

## 6. Test your CV with YourSmartCV (free scan)

With YourSmartCV you can:

- upload your current CV (even if it is not ATS friendly)  
- see how an ATS reads it  
- get a match score  
- generate an improved, ready-to-use ATS friendly CV  

👉 Start with the [free ATS scan](/en/ats-scan/) and see how much of your profile a real ATS can read.
`,
      fr: `
Quand vous candidatez en ligne, votre CV passe souvent d’abord par un logiciel ATS avant d’être lu par une personne.

Si l’ATS ne peut pas lire correctement votre CV, vous n’apparaissez pas dans les recherches des recruteurs, même avec un bon profil.

Ce guide explique comment créer un CV compatible ATS qui fonctionne dans plusieurs pays et langues. À la fin, vous verrez comment le tester avec un scan ATS dans YourSmartCV.

> Pour mieux comprendre ces outils, lisez aussi notre article sur [ce qu’est un ATS et comment les recruteurs l’utilisent vraiment](/fr/blog/quest-ce-quun-ats-et-comment-les-recruteurs-lutilisent/).

## 1. Ce que fait un ATS avec votre CV

Un ATS est un logiciel que les entreprises utilisent pour :

- stocker les CV et lettres de motivation  
- lire et extraire les informations importantes  
- rechercher les candidats par mots-clés  
- classer les profils selon leur adéquation avec le poste  

Si le CV n’est pas compatible ATS, le système peut :

- mal lire votre texte  
- ignorer des compétences importantes  
- vous placer très bas dans les résultats  

L’objectif : un CV facile à lire pour un logiciel **et** pour un humain.

## 2. Structure sûre pour un CV compatible ATS

Structure recommandée :

- Coordonnées  
- Résumé professionnel / profil  
- Compétences clés  
- Expériences  
- Formation  
- Sections optionnelles : langues, certifications, outils  

### Coordonnées

Indiquez :

- nom et prénom  
- ville et pays  
- e-mail  
- téléphone avec indicatif  
- liens (LinkedIn, portfolio)  

Écrivez ces informations en texte normal, pas dans l’en-tête ou le pied de page.

### Résumé professionnel

En 3–5 lignes, précisez :

- votre métier et vos années d’expérience  
- votre domaine principal (data, marketing, finance…)  
- le type de poste visé  
- 1–2 forces clés  

Intégrez quelques mots-clés issus des offres ciblées.

### Compétences clés

Regroupez-les par thème :

- Méthodes : A/B testing, prévisions, MMM  
- Outils : Python, R, SQL, Power BI, Tableau  
- Business : marketing de performance, pricing, analytics  
- Langues : français C2, anglais C1, allemand B1, etc.  

### Expérience

Pour chaque poste :

- intitulé du poste  
- nom de l’entreprise, ville, pays  
- dates (mois/année)  
- courte phrase de contexte  
- 4 à 7 puces de résultats  

Mettez des chiffres quand c’est possible.

### Formation et sections optionnelles

Indiquez diplôme, établissement, pays, dates.  
Ajoutez éventuellement :

- Langues  
- Certifications / formations  
- Outils & technologies  

Utilisez des listes plutôt que de longs paragraphes.

## 3. Règles de mise en forme

Pour rester sûr côté ATS :

- mise en page simple, une colonne  
- polices classiques (Arial, Calibri…)  
- pas d’icônes importantes sans texte  
- pas de CV entièrement conçu comme une image  

En général, envoyez votre CV en PDF (sauf indication contraire).

## 4. Utiliser les mots-clés des offres

Repérez dans l’annonce :

- l’intitulé exact du poste  
- les compétences demandées  
- les outils cités  
- les termes du secteur  
- les langues exigées  

Intégrez-les dans :

- le résumé  
- la section compétences  
- des puces d’expérience  

Sans copier l’annonce mot à mot.

## 5. Erreurs fréquentes

Parmi les erreurs qui bloquent ATS et recruteurs :

- design trop chargé  
- informations clés dans des tableaux ou images  
- aucun chiffre dans l’expérience  
- CV de 4–5 pages  

Pour une liste détaillée, consultez les [dix erreurs fréquentes sur le CV qui bloquent ATS et recruteurs](/fr/blog/erreurs-cv-qui-bloquent-ats-et-recruteurs/).

## 6. Tester votre CV avec YourSmartCV

Avec YourSmartCV vous pouvez :

- téléverser votre CV actuel  
- lancer un scan ATS gratuit  
- voir votre score de correspondance  
- générer une version plus claire et compatible  

👉 Commencez avec le [scan ATS gratuit YourSmartCV](/fr/ats-scan/).
`,
      de: `
Wenn Sie sich heute online bewerben, landet Ihr Lebenslauf oft zuerst in einem ATS – einem Applicant Tracking System – bevor ihn jemand liest.

Kann das ATS Ihren Lebenslauf nicht richtig auslesen, tauchen Sie in Suchergebnissen von Recruitern kaum auf, selbst wenn Sie gut geeignet wären.

Dieser Leitfaden zeigt, wie Sie einen ATS freundlichen Lebenslauf erstellen, der in verschiedenen Ländern funktioniert. Am Ende erfahren Sie, wie Sie ihn mit einem ATS-Scan in YourSmartCV testen.

> Für mehr Hintergrund lesen Sie zuerst [was ein ATS ist und wie Recruiter es wirklich nutzen](/de/blog/was-ist-ein-ats-und-wie-nutzen-recruiter-es/).

## 1. Was ein ATS mit Ihrem Lebenslauf macht

Ein ATS:

- speichert Lebensläufe und Anschreiben  
- liest Text aus Dokumenten aus  
- legt Kandidatenprofile an  
- ermöglicht Suchen nach Stichwörtern  
- hilft beim Filtern nach Kriterien  

Ist Ihr Lebenslauf schlecht aufgebaut, liest das System wichtige Inhalte nicht oder falsch.

## 2. Sichere Struktur für einen ATS freundlichen Lebenslauf

Bewährt hat sich:

- Kontaktdaten  
- Profil / Zusammenfassung  
- Kernkompetenzen  
- Berufserfahrung  
- Ausbildung  
- optionale Abschnitte: Sprachen, Zertifikate, Tools  

### Kontaktdaten

- Name  
- Ort, Land  
- E-Mail  
- Telefonnummer  
- optional: LinkedIn, Portfolio  

Alles als normalen Text, nicht in Kopf- oder Fußzeile.

### Profil

In 3–5 Sätzen:

- wer Sie sind  
- Ihr Schwerpunkt (z. B. Data Science, Marketing)  
- welche Rolle Sie suchen  
- 1–2 Schwerpunktthemen  

### Kompetenzen

Als übersichtliche Liste, z. B.:

- Methoden: A/B-Tests, Forecasting, MMM  
- Tools: Python, R, SQL, Power BI  
- Business: Performance Marketing, Pricing  

### Berufserfahrung

Für jede Station:

- Funktionsbezeichnung  
- Unternehmen, Ort, Land  
- Zeitraum (MM/JJJJ – MM/JJJJ)  
- ein Satz zur Rolle  
- 4–7 Aufzählungspunkte mit Ergebnissen  

Zahlen helfen: Prozent, Beträge, Zeitersparnis.

### Ausbildung / optionale Bereiche

- Abschlüsse mit Hochschule und Zeitraum  
- Sprachen, Zertifikate, Tools & Technologien als Listen

## 3. Formatierung

Für ATS Sicherheit:

- einspaltige, klare Struktur  
- Standard-Schriftarten  
- keine wichtigen Infos nur als Grafik oder Symbol  
- PDF-Format, sofern nichts anderes verlangt wird  

## 4. Schlüsselwörter aus Stellenanzeigen

Markieren Sie in der Anzeige:

- Jobtitel  
- Skills  
- Tools  
- Branchenbegriffe  
- Sprachanforderungen  

Bauen Sie diese Begriffe sinnvoll ein – vor allem in Profil, Skills und Erfahrung.

## 5. Häufige Fehler

Typisch sind:

- zu komplexes Design  
- fehlende oder unklare Jobtitel  
- keine messbaren Ergebnisse  
- zu lange Lebensläufe  

Mehr dazu in den [zehn häufigen Fehlern im Lebenslauf, die ATS und Recruiter blockieren](/de/blog/haeufige-fehler-im-lebenslauf-die-ats-blockieren/).

## 6. ATS-Check mit YourSmartCV

Mit YourSmartCV können Sie:

- Ihren Lebenslauf hochladen  
- sehen, wie ein ATS ihn liest  
- einen Match-Score erhalten  
- eine optimierte Version generieren  

👉 Nutzen Sie den [kostenlosen ATS-Scan](/de/ats-scan/), um zu sehen, wie gut Ihr Profil ankommt.
`,
      it: `
Quando invii il tuo CV online, in molte aziende viene prima letto da un sistema ATS prima di arrivare a un selezionatore.

Se l’ATS non riesce a leggere bene il documento, è possibile che tu non compaia quasi mai nelle ricerche, anche con un ottimo profilo.

Questa guida spiega come creare un CV compatibile con gli ATS che funzioni in diversi paesi. Alla fine vedrai come testarlo con uno scan ATS in YourSmartCV.

> Per il contesto generale, leggi anche [che cosa è un ATS e come lo usano davvero i recruiter](/it/blog/che-cosa-e-un-ats-e-come-lo-usano-i-recruiter/).

## 1. Cosa fa un ATS con il tuo CV

Un ATS:

- archivia CV e lettere di presentazione  
- estrae testi e informazioni chiave  
- permette ricerche per parole chiave  
- aiuta a filtrare i candidati  

Se il CV usa una struttura troppo complessa o non contiene le parole importanti, il sistema lo legge male e il profilo appare poco.

## 2. Struttura consigliata

Una struttura sicura:

- Dati di contatto  
- Riepilogo professionale  
- Competenze chiave  
- Esperienze lavorative  
- Formazione  
- Sezioni opzionali: lingue, certificazioni, strumenti  

### Dati di contatto

Nome e cognome, città e paese, e-mail, telefono con prefisso, link a LinkedIn o portfolio.  
Scritti come testo normale, non in testata o piè di pagina.

### Riepilogo

3–5 righe su:

- ruolo e anni di esperienza  
- area principale (data, marketing, prodotto…)  
- tipo di posizione cercata  
- 1–2 punti di forza  

### Competenze

Liste chiare, es.:

- Metodi: A/B test, forecasting, MMM  
- Strumenti: Python, R, SQL  
- Business: performance marketing, analisi clienti  

### Esperienze

Per ogni esperienza:

- titolo di ruolo  
- azienda, città, paese  
- periodo (mese/anno)  
- breve frase di contesto  
- 4–7 punti con risultati e numeri  

### Formazione e sezioni extra

Indica titolo di studio, istituto, paese e date.  
Aggiungi, se utile, lingue, certificazioni, strumenti e tecnologie.

## 3. Regole di formattazione

Per restare “amico” degli ATS:

- layout semplice a una colonna  
- font standard (Arial, Calibri, ecc.)  
- niente informazioni importanti solo in tabelle/grafici  
- CV salvato in PDF, salvo richiesta diversa  

## 4. Parole chiave dalle offerte di lavoro

Evidenzia nell’annuncio:

- titolo di ruolo  
- competenze richieste  
- strumenti e piattaforme  
- termini del settore  
- lingue e certificazioni  

Inserisci questi elementi in riepilogo, competenze e punti esperienza, senza copiare tutto l’annuncio.

## 5. Errori comuni

Tra gli errori più frequenti:

- grafica troppo complessa  
- titoli poco chiari  
- nessun risultato misurabile  
- CV molto lungo e generico  

Per esempi concreti consulta i [dieci errori comuni nel CV che bloccano ATS e selezionatori](/it/blog/errori-comuni-del-cv-che-bloccano-ats/).

## 6. Testare il CV con YourSmartCV

Con YourSmartCV puoi:

- caricare il CV attuale  
- vedere come lo interpreta un sistema ATS  
- ottenere un punteggio di compatibilità  
- generare una versione più efficace  

👉 Prova lo [scan ATS gratuito di YourSmartCV](/it/ats-scan/).
`,
      es: `
Cuando envías tu CV en línea, muchas empresas lo leen primero con un sistema ATS, un software de seguimiento de candidatos.

Si el ATS no puede leer bien tu CV, es posible que no aparezcas en las búsquedas de los reclutadores, aunque tengas un buen perfil.

Esta guía explica cómo crear un CV compatible con ATS que funcione en diferentes países e idiomas. Al final verás cómo probar tu CV con un escaneo ATS en YourSmartCV.

> Si quieres entender mejor el sistema, puedes leer también nuestro artículo sobre [qué es un ATS y cómo lo usan realmente los reclutadores](/es/blog/que-es-un-ats-y-como-lo-usan-los-reclutadores/).

## Ideas principales

- estructura clara con datos de contacto, resumen, habilidades, experiencia y estudios  
- formato sencillo sin columnas complicadas ni gráficos  
- uso de palabras clave sacadas de las ofertas de trabajo  
- guardar el CV en formato PDF  
- utilizar el escaneo ATS de YourSmartCV para ver el nivel de coincidencia  

Para conocer errores concretos que bloquean tu candidatura, consulta los [diez errores comunes en el CV que bloquean ATS y reclutadores](/es/blog/errores-comunes-en-el-cv-que-bloquean-ats/).

👉 Empieza con el [escaneo ATS gratuito de YourSmartCV](/es/ats-scan/).
`,
    },
  },

  // ---------------------------------------------------------------------------
  // ARTICLE 2 – Tailor CV to job description with AI
  // (this is the long version in all 5 languages)
  // ---------------------------------------------------------------------------
  {
    id: "tailor-cv-ai",
    cluster: "ats-core",
    date: "2025-12-02",
    readTime: "6 min",
    tags: ["AI", "ATS", "Job search"],
    categoryLabel: {
      en: "AI CV Tailoring",
      fr: "Adaptation du CV avec l’IA",
      de: "Lebenslauf & KI",
      it: "Adattare il CV con l’IA",
      es: "Adaptar el CV con IA",
    },
    slug: {
      en: "tailor-cv-to-job-description-with-ai",
      fr: "adapter-votre-cv-a-une-offre-avec-ia",
      de: "lebenslauf-an-stellenausschreibung-mit-ki-anpassen",
      it: "adattare-cv-annuncio-di-lavoro-con-ia",
      es: "adaptar-cv-a-una-oferta-con-ia",
    },
    h1: {
      en: "How To Tailor Your CV To Any Job Description With AI",
      fr: "Comment adapter votre CV à une offre d’emploi avec l’aide de l’IA",
      de: "Wie Sie Ihren Lebenslauf mit Hilfe von KI an eine Stellenausschreibung anpassen",
      it: "Come adattare il tuo CV a un annuncio di lavoro con l’IA",
      es: "Cómo adaptar tu CV a una oferta de trabajo con ayuda de la IA",
    },
    metaTitle: {
      en: "How To Tailor Your CV To A Job Description With AI",
      fr: "Adapter son CV à une offre avec l’IA",
      de: "Lebenslauf mit KI an eine Stelle anpassen",
      it: "Adattare il CV a un annuncio con l’IA",
      es: "Adaptar el CV a una oferta con IA",
    },
    metaDescription: {
      en: "Learn how to tailor your CV to any job description with AI, match ATS keywords, and create a recruiter-ready CV in minutes using YourSmartCV.",
      fr: "Apprenez à adapter votre CV à une offre d’emploi grâce à l’IA, à mieux couvrir les mots-clés ATS et à créer un CV prêt pour le recruteur avec YourSmartCV.",
      de: "Erfahren Sie, wie Sie Ihren Lebenslauf mit KI an eine Stellenausschreibung anpassen, ATS-Schlüsselwörter abdecken und einen recruiterfreundlichen Lebenslauf mit YourSmartCV erstellen.",
      it: "Scopri come adattare il tuo CV a un annuncio di lavoro con l’IA, coprire meglio le parole chiave degli ATS e creare un CV pronto per il selezionatore con YourSmartCV.",
      es: "Aprende a adaptar tu CV a una oferta de trabajo usando IA, cubrir mejor las palabras clave de ATS y crear un CV listo para el reclutador con YourSmartCV.",
    },
    body: {
      en: `
Sending the same CV to every job is one of the main reasons candidates never get called.

Recruiters and ATS tools compare your CV with the job description. If your skills and language do not match what the company writes, you look less relevant, even when you could do the job.

This guide shows how to adapt your CV to any job description and how to use AI in YourSmartCV to do most of the work for you.

> Before tailoring, make sure your CV is already [ATS friendly in the first place](/en/blog/ats-friendly-cv-any-country/).

## 1. Build a simple keyword map from the job

Take the job description and highlight:

- main job title and seniority  
- required skills  
- tools and platforms  
- industry or domain  
- language requirements  

Write these in a list. This is your keyword map for this specific job.

## 2. Compare your current CV with the map

Open your CV next to the job description and ask:

- does my summary mention the same type of role?  
- do my skills match the top skills in the map?  
- do my experience bullets show clear evidence for those skills?  
- are the main tools visible on the first page?  
- is my language level clear?  

Most people see that their CV is too generic and does not speak directly to this role.

## 3. Use AI to create a first tailored draft

In **YourSmartCV** you can:

- upload your current CV as PDF  
- paste the job description  
- select your language  

The AI engine then:

- extracts your experience and skills  
- aligns them with the keyword map  
- creates a fresh ATS friendly CV draft focused on this role  
- can also create a matching cover letter  

This gives you a strong starting point in a few minutes instead of hours.

## 4. Review and correct the AI version

You stay in control. Go through the draft and:

- delete anything that is not true  
- fix dates and titles  
- adjust wording where needed  
- add numbers and concrete results that only you know  

The goal is a CV that is sharper and more relevant, but still honest and personal.

If you also want a letter, follow our guide on [how to write a cover letter that matches your CV and the job description](/en/blog/cover-letter-that-matches-your-cv-and-job-description/).

## 5. Run an ATS match check

When you are happy with the tailored CV, run an ATS match check in YourSmartCV with the same job description.

You will see:

- your match score  
- which skills and keywords are already covered  
- which important terms are missing  

You can then make small edits to close the gap before you click apply.

With this workflow you move from a generic CV to a focused, AI assisted and ATS aware CV for each job, without spending all your time in a text editor.

👉 Start here: [YourSmartCV AI matching & ATS scan](/en/ats-scan/).
`,
      fr: `
Envoyer exactement le même CV pour toutes les offres est l’une des principales raisons pour lesquelles les candidats ne reçoivent jamais d’appel.

Les recruteurs et les ATS comparent votre CV au texte de l’offre. Si vos compétences et votre vocabulaire ne reflètent pas ce que l’entreprise cherche, vous semblez moins pertinent, même si vous êtes capable de faire le travail.

Ce guide explique comment adapter votre CV à n’importe quelle offre d’emploi et comment utiliser l’IA de **YourSmartCV** pour faire une grande partie du travail à votre place.

> Avant de personnaliser, assurez-vous que votre CV est déjà [compatible ATS](/fr/blog/cv-compatible-ats-tous-pays/).

## 1. Construire une carte de mots-clés à partir de l’offre

Prenez l’offre d’emploi et surlignez :

- l’intitulé de poste et le niveau (junior, senior, lead, etc.)  
- les compétences indispensables  
- les outils et plateformes cités  
- le secteur ou domaine  
- les langues et exigences spécifiques  

Regroupez tout cela dans une liste : c’est votre carte de mots-clés pour ce poste précis.

## 2. Comparer votre CV avec cette carte

Ouvrez votre CV à côté de l’offre et demandez-vous :

- est-ce que mon résumé mentionne un rôle du même type ?  
- est-ce que mes compétences couvrent les principaux points de la carte ?  
- est-ce que mes puces d’expérience prouvent vraiment ces compétences ?  
- est-ce que les outils importants sont visibles dès la première page ?  
- est-ce que mon niveau de langue est clair ?  

La plupart des gens découvrent que leur CV est trop générique et ne parle pas directement à ce poste.

## 3. Utiliser l’IA pour générer un premier brouillon ciblé

Dans **YourSmartCV**, vous pouvez :

- téléverser votre CV au format PDF  
- coller le texte de l’offre  
- choisir la langue de sortie  

Le moteur d’IA va ensuite :

- extraire vos expériences et compétences  
- les aligner avec la carte de mots-clés  
- créer un nouveau CV compatible ATS centré sur ce poste  
- éventuellement générer aussi une lettre de motivation assortie  

Vous obtenez ainsi une première version solide en quelques minutes au lieu de plusieurs heures.

## 4. Relire et corriger la version IA

Vous restez toujours maître du contenu. Parcourez le brouillon et :

- supprimez tout ce qui n’est pas vrai  
- corrigez les dates, intitulés de poste et noms d’entreprise  
- ajustez les formulations pour qu’elles sonnent comme vous  
- ajoutez des chiffres et résultats concrets que vous seul connaissez  

L’objectif : un CV plus précis, plus pertinent, mais toujours honnête et personnel.

Si vous préparez aussi une lettre, découvrez notre guide sur [comment écrire une lettre de motivation cohérente avec votre CV et l’offre](/fr/blog/lettre-de-motivation-coherente-avec-cv-et-offre/).

## 5. Lancer une analyse de correspondance ATS

Une fois le CV adapté, lancez un scan de correspondance ATS dans YourSmartCV avec la même offre.

Vous verrez :

- votre score de correspondance  
- les compétences et mots-clés déjà couverts  
- les termes importants encore manquants  

Vous pouvez alors faire quelques ajustements avant de cliquer sur « Postuler ».

👉 Commencez ici : [YourSmartCV – IA de mise en correspondance et scan ATS](/fr/ats-scan/).
`,
      de: `
Viele Bewerber nutzen für alle Stellen dieselbe Version ihres Lebenslaufs. Das ist bequem, führt aber oft dazu, dass sie nie angerufen werden.

Ein ATS und der Recruiter vergleichen die Stellenausschreibung mit Ihrem Dokument. Wenn die wichtigen Begriffe und Fähigkeiten dort nicht vorkommen, wirkt Ihr Profil weniger passend – selbst wenn Sie die Aufgaben eigentlich erfüllen könnten.

Dieser Leitfaden zeigt, wie Sie Ihren Lebenslauf an jede Stellenausschreibung anpassen und wie Ihnen die KI von **YourSmartCV** dabei einen Großteil der Arbeit abnimmt.

> Bevor Sie anpassen, stellen Sie sicher, dass Ihr Lebenslauf bereits [ATS freundlich aufgebaut ist](/de/blog/ats-freundlicher-lebenslauf-fuer-jedes-land/).

## 1. Eine einfache Schlüsselwort-Karte aus der Anzeige bauen

Markieren Sie in der Stellenanzeige:

- Stellenbezeichnung und Senioritätslevel  
- geforderte Fähigkeiten  
- genannte Tools und Plattformen  
- Branche oder Themengebiet  
- Sprach- und Standortanforderungen  

Schreiben Sie alles in eine Liste. Das ist Ihre Schlüsselwort-Karte.

## 2. Ihren Lebenslauf mit der Karte vergleichen

Legen Sie Lebenslauf und Anzeige nebeneinander und fragen Sie sich:

- spiegelt mein Profil dieselbe Art von Rolle wider?  
- decken meine Skills die wichtigsten Punkte der Karte ab?  
- zeigen die Aufzählungspunkte klare Belege?  
- sind die wichtigsten Tools auf der ersten Seite sichtbar?  
- ist mein Sprachniveau erkennbar?  

Oft stellt man fest, dass der Lebenslauf zu allgemein gehalten ist.

## 3. Mit KI einen ersten Entwurf erstellen

In **YourSmartCV** können Sie:

- Ihren aktuellen Lebenslauf als PDF hochladen  
- den Text der Stellenausschreibung einfügen  
- die gewünschte Sprache wählen  

Die KI:

- extrahiert Erfahrungen und Kompetenzen  
- ordnet sie der Schlüsselwort-Karte zu  
- erstellt einen neuen, ATS freundlichen Entwurf, der auf diese Stelle fokussiert  
- kann zusätzlich ein passendes Anschreiben erzeugen  

So erhalten Sie in wenigen Minuten einen starken ersten Entwurf.

## 4. Die KI-Version prüfen und anpassen

Sie behalten die Kontrolle. Gehen Sie den Entwurf durch und:

- löschen Sie alles, was nicht stimmt  
- korrigieren Sie Daten, Funktionsbezeichnungen und Firmennamen  
- passen Sie Formulierungen an Ihre Sprache an  
- ergänzen Sie Kennzahlen und konkrete Ergebnisse  

Ziel ist ein schärferer, relevanterer Lebenslauf, der ehrlich bleibt.

Wenn Sie auch ein Anschreiben brauchen, lesen Sie unseren Leitfaden dazu, [wie Sie ein Anschreiben verfassen, das zu Ihrem Lebenslauf und zur Stelle passt](/de/blog/anschreiben-das-zu-lebenslauf-und-stelle-passt/).

## 5. Einen ATS-Match-Check durchführen

Sind Sie mit dem angepassten Lebenslauf zufrieden, führen Sie in YourSmartCV einen ATS-Abgleich mit derselben Anzeige durch.

Sie sehen:

- Ihren Matching-Score  
- welche Skills und Schlüsselwörter abgedeckt sind  
- welche Begriffe noch fehlen  

Dann können Sie letzte Korrekturen vornehmen, bevor Sie sich bewerben.

👉 Starten Sie hier: [YourSmartCV – KI-Matching & ATS-Scan](/de/ats-scan/).
`,
      it: `
Inviare sempre lo stesso CV per tutte le offerte è uno dei motivi principali per cui molti candidati non vengono mai richiamati.

I selezionatori e i sistemi ATS confrontano il tuo CV con il testo dell’annuncio. Se competenze e linguaggio non riflettono ciò che l’azienda cerca, il tuo profilo sembra meno adatto, anche se in realtà potresti svolgere il lavoro.

Questa guida spiega come adattare il tuo CV a qualsiasi annuncio e come usare l’IA di **YourSmartCV** per fare gran parte del lavoro al tuo posto.

> Prima di personalizzare, assicurati che il tuo CV sia già [compatibile con gli ATS](/it/blog/cv-compatibile-ats-per-ogni-paese/).

## 1. Creare una mappa di parole chiave

Prendi l’annuncio di lavoro ed evidenzia:

- titolo di lavoro e livello  
- competenze richieste  
- strumenti e piattaforme citati  
- settore o area di attività  
- requisiti linguistici  

Metti tutto in un elenco: è la tua mappa di parole chiave per questa posizione.

## 2. Confrontare il CV con la mappa

Apri il CV accanto all’annuncio e chiediti:

- il riepilogo iniziale menziona un ruolo dello stesso tipo?  
- le mie competenze coprono i punti principali della mappa?  
- i bullet dell’esperienza dimostrano davvero queste competenze?  
- gli strumenti principali sono visibili già nella prima pagina?  
- il mio livello di lingua è indicato chiaramente?  

Molti scoprono che il loro CV è troppo generico.

## 3. Usare l’IA per creare un primo draft

In **YourSmartCV** puoi:

- caricare il tuo CV in PDF  
- incollare la descrizione dell’offerta  
- scegliere la lingua di output  

Il motore di IA:

- estrae esperienze e competenze  
- le allinea con la mappa di parole chiave  
- crea una nuova bozza di CV compatibile ATS centrata su questo ruolo  
- può generare anche una lettera di presentazione abbinata  

## 4. Rivedere e correggere la versione generata

Il controllo rimane tuo. Passa in rassegna la bozza e:

- elimina ciò che non è vero  
- correggi date, titoli e nomi aziendali  
- modifica le frasi perché suonino naturali  
- aggiungi numeri e risultati che solo tu conosci  

Se ti serve anche una lettera, leggi come [scrivere una lettera di presentazione in linea con il tuo CV e con l’annuncio](/it/blog/lettera-di-presentazione-in-linea-con-cv-e-annuncio/).

## 5. Eseguire un controllo ATS

Quando sei soddisfatto, esegui in YourSmartCV uno scan ATS con lo stesso annuncio.

Vedrai:

- punteggio di corrispondenza  
- competenze e parole chiave già presenti  
- termini importanti ancora mancanti  

👉 Inizia da qui: [YourSmartCV – matching IA e scan ATS](/it/ats-scan/).
`,
      es: `
Enviar exactamente el mismo CV para todas las ofertas es una de las razones principales por las que muchos candidatos nunca reciben una llamada.

Los reclutadores y los sistemas ATS comparan tu CV con la descripción del puesto. Si tus competencias y tu vocabulario no reflejan lo que la empresa pide, pareces menos relevante, aunque podrías hacer bien el trabajo.

Esta guía te muestra cómo adaptar tu CV a cualquier oferta y cómo usar la IA de **YourSmartCV** para hacer gran parte del trabajo en pocos minutos.

> Antes de personalizar, asegúrate de que tu CV ya es [compatible con ATS](/es/blog/cv-compatible-ats-para-cualquier-pais/).

## 1. Crear un mapa de palabras clave

Toma la oferta y subraya:

- el puesto y el nivel  
- las habilidades imprescindibles  
- las herramientas y plataformas mencionadas  
- el sector o área de actividad  
- los requisitos de idioma  

Pon todo en una lista: es tu mapa de palabras clave.

## 2. Comparar tu CV con ese mapa

Abre tu CV junto a la oferta y pregúntate:

- ¿mi resumen menciona un rol del mismo tipo?  
- ¿mis habilidades cubren los puntos principales del mapa?  
- ¿los puntos de experiencia demuestran claramente esas habilidades?  
- ¿las herramientas clave se ven ya en la primera página?  
- ¿mi nivel de idioma queda claro?  

## 3. Usar la IA para crear una primera versión adaptada

En **YourSmartCV** puedes:

- subir tu CV en PDF  
- pegar la descripción del puesto  
- elegir el idioma  

La IA:

- extrae tu experiencia y habilidades  
- las alinea con el mapa de palabras clave  
- genera una nueva versión de CV compatible con ATS centrada en ese puesto  
- puede crear también una carta de presentación a juego  

## 4. Revisar y corregir la versión generada

Revisa la versión y:

- elimina todo lo que no sea cierto  
- corrige fechas, cargos y nombres de empresa  
- ajusta el tono para que suene como tú  
- añade números y resultados concretos que solo tú conoces  

Si también necesitas carta, consulta cómo [escribir una carta de presentación que encaje con tu CV y con la oferta](/es/blog/carta-de-presentacion-que-encaja-con-cv-y-oferta/).

## 5. Hacer un chequeo ATS

Cuando estés satisfecho, realiza en YourSmartCV un escaneo ATS con la misma oferta.

Verás:

- tu porcentaje de coincidencia  
- qué habilidades y palabras clave ya están cubiertas  
- qué términos importantes faltan todavía  

👉 Empieza aquí: [YourSmartCV – IA de matching y escaneo ATS](/es/ats-scan/).
`,
    },
  },

  // ---------------------------------------------------------------------------
  // ARTICLE 3 – What is an ATS
  // ---------------------------------------------------------------------------
  {
    id: "what-is-ats",
    cluster: "ats-core",
    date: "2025-12-02",
    readTime: "6 min",
    tags: ["ATS", "Recruiters"],
    categoryLabel: {
      en: "ATS Basics",
      fr: "Bases de l’ATS",
      de: "ATS-Grundlagen",
      it: "Nozioni base sugli ATS",
      es: "Conceptos básicos de ATS",
    },
    slug: {
      en: "what-is-an-ats-and-how-recruiters-use-it",
      fr: "quest-ce-quun-ats-et-comment-les-recruteurs-lutilisent",
      de: "was-ist-ein-ats-und-wie-nutzen-recruiter-es",
      it: "che-cosa-e-un-ats-e-come-lo-usano-i-recruiter",
      es: "que-es-un-ats-y-como-lo-usan-los-reclutadores",
    },
    h1: {
      en: "What Is An ATS And How Recruiters Really Use It",
      fr: "Qu’est-ce qu’un ATS et comment les recruteurs l’utilisent vraiment",
      de: "Was ist ein ATS und wie nutzen Recruiter es wirklich",
      it: "Che cosa è un ATS e come lo usano davvero i recruiter",
      es: "Qué es un ATS y cómo lo usan realmente los reclutadores",
    },
    metaTitle: {
      en: "What Is An ATS And How Recruiters Use It",
      fr: "Qu’est-ce qu’un ATS et comment les recruteurs l’utilisent",
      de: "Was ist ein ATS und wie nutzen Recruiter es",
      it: "Che cosa è un ATS e come lo usano i recruiter",
      es: "Qué es un ATS y cómo lo usan los reclutadores",
    },
    metaDescription: {
      en: "Learn what an ATS is, how recruiters really use it, and what this means for your CV and job search. Includes a free ATS scan with YourSmartCV.",
      fr: "Découvrez ce qu’est un ATS, comment les recruteurs l’utilisent vraiment et ce que cela signifie pour votre CV. Analyse ATS gratuite avec YourSmartCV.",
      de: "Erfahren Sie, was ein ATS ist, wie Recruiter es wirklich nutzen und was das für Ihren Lebenslauf bedeutet. Mit kostenlosem ATS-Scan in YourSmartCV.",
      it: "Scopri che cosa è un ATS, come lo usano i recruiter e che cosa questo significa per il tuo CV. Include uno scan ATS gratuito con YourSmartCV.",
      es: "Aprende qué es un ATS, cómo lo usan los reclutadores y qué implica para tu CV. Incluye un escaneo ATS gratuito con YourSmartCV.",
    },
    body: {
      en: `
If you apply for jobs online today, your CV does not go straight to a person. In many companies it goes first into an ATS, an applicant tracking system.

Understanding how an ATS works helps you understand why your CV needs a clear structure and the right keywords, and why YourSmartCV focuses on ATS friendly CVs.

> When you are ready to act on this, read our guide on [creating an ATS friendly CV that works in any country](/en/blog/ats-friendly-cv-any-country/).

## 1. What is an ATS?

An ATS is software that companies and recruiters use to manage applications. It replaces manual tracking in spreadsheets and email. A modern ATS can:

- store thousands of CVs and cover letters  
- read and extract information from documents  
- create candidate profiles with skills and experience  
- help recruiters search and filter candidates  
- track every stage of the process from application to offer  

Think of it as a search engine and database for candidates.

## 2. How recruiters really use an ATS

Most recruiters do not read every CV one by one. They use the ATS in three ways.

### 2.1 Search by keywords

Recruiters type keywords that match the job description:

- job titles such as “data scientist”, “marketing manager”  
- tools such as Python, Salesforce, Excel  
- industries such as ecommerce or telecom  
- languages such as English, German, Spanish  

The ATS returns a list of candidates who mention these terms.

### 2.2 Filter by basic criteria

Recruiters add filters for:

- location or region  
- years of experience  
- education level  
- language level  

Candidates who do not meet these filters may never be reviewed.

### 2.3 Review a shortlist

From the filtered list, recruiters open a smaller number of CVs and scan:

- summary / profile  
- experience bullets  
- skills list  

So the ATS does not “reject” you alone, but it strongly controls who is seen first.

## 3. Why this matters for your CV

If your CV:

- uses an unusual layout  
- hides information in graphics  
- does not contain the right keywords  

then the ATS may parse it badly or never show it near the top of the list.

That is why:

- clear sections  
- simple formatting  
- and relevant keywords  

are more important than fancy design.

## 4. How YourSmartCV uses ATS logic

YourSmartCV is built with ATS behaviour in mind. It:

- parses your existing CV  
- rebuilds it into a clean ATS friendly format  
- helps you use the right keywords for each job  
- offers a free ATS scan so you can see what a system actually reads  

For step-by-step formatting advice, see [how to create an ATS friendly CV that works in any country](/en/blog/ats-friendly-cv-any-country/).
`,
      fr: `
Lorsque vous postulez en ligne, votre CV arrive rarement directement dans la boîte mail d’un recruteur. Dans beaucoup d’entreprises, il est d’abord stocké dans un ATS.

Comprendre comment fonctionne un ATS permet de comprendre pourquoi la structure et les mots-clés de votre CV sont si importants.

## 1. Qu’est-ce qu’un ATS ?

Un Applicant Tracking System est un logiciel de gestion des candidatures. Il permet de :

- centraliser les CV et lettres de motivation  
- extraire les informations essentielles  
- rechercher des candidats par mots-clés  
- suivre les candidatures jusqu’à l’embauche  

On peut le voir comme un moteur de recherche pour CV.

## 2. Comment les recruteurs l’utilisent vraiment

La plupart des recruteurs ne lisent pas tous les CV. Ils :

1. recherchent des candidats par mots-clés (intitulés de poste, outils, secteur)  
2. appliquent des filtres (lieu, années d’expérience, niveau d’études, langues)  
3. ouvrent uniquement une short-list de profils jugés pertinents  

Si votre CV ne contient pas les bons termes ou si le format perturbe la lecture, vous apparaissez rarement dans cette short-list.

## 3. Ce que cela signifie pour votre CV

Un CV efficace pour l’ATS doit :

- avoir des sections claires  
- utiliser un format simple, sans visuels complexes  
- contenir les mots-clés principaux des offres visées  

Sinon, le système peut mal vous lire ou vous placer tout en bas des résultats.

Pour transformer ce constat en actions concrètes, suivez notre guide sur [comment créer un CV compatible avec les ATS pour n’importe quel pays](/fr/blog/cv-compatible-ats-tous-pays/).

👉 Vous pouvez aussi lancer un [scan ATS gratuit](/fr/ats-scan/) via YourSmartCV pour voir comment un vrai système interprète votre CV.
`,
      de: `
Wenn Sie sich online bewerben, landet Ihr Lebenslauf meist zuerst in einem Bewerbermanagementsystem – einem ATS.

## 1. Was macht ein ATS?

Ein ATS:

- sammelt Bewerbungen an einem Ort  
- liest Daten wie Positionen, Zeiträume und Skills aus  
- ermöglicht Volltextsuchen und Filter  
- unterstützt Recruiter bei der Organisation des Prozesses  

Es ist im Kern eine Suchmaschine für Lebensläufe.

## 2. Wie arbeiten Recruiter damit?

Typische Schritte:

1. **Suche nach Stichwörtern** – Jobtitel, Tools, Branchenbegriffe, Sprachen  
2. **Filter** – Standort, Berufserfahrung, Ausbildung, Sprachlevel  
3. **Sichtung einer Auswahl** – die am besten passenden Profile werden geöffnet und kurz geprüft  

Wenn Ihr Lebenslauf die relevanten Begriffe nicht enthält oder schwer auszulesen ist, landen Sie selten in dieser engen Auswahl.

## 3. Konsequenzen für Ihren Lebenslauf

Wichtig sind:

- klare Struktur (Profil, Erfahrung, Skills, Ausbildung)  
- lesbares Layout ohne komplizierte Tabellen oder Grafiken  
- gezielter Einsatz von Schlüsselwörtern aus der Stellenanzeige  

Mehr dazu finden Sie in unserem Leitfaden zum [ATS freundlichen Lebenslauf für jedes Land](/de/blog/ats-freundlicher-lebenslauf-fuer-jedes-land/).

👉 Mit dem [kostenlosen ATS-Scan von YourSmartCV](/de/ats-scan/) sehen Sie, wie ein System Ihren Lebenslauf interpretiert.
`,
      it: `
Quando invii il tuo CV online, spesso viene memorizzato prima in un sistema ATS e solo dopo visto da una persona.

## Che cosa fa un ATS?

Un ATS:

- raccoglie CV e candidature  
- estrae ruoli, date, competenze  
- permette ricerche per parole chiave  
- aiuta i recruiter a filtrare i profili  

In pratica è un motore di ricerca per candidati.

## Come lo usano i recruiter?

Di solito:

1. cercano parole chiave prese dall’annuncio (titoli di ruolo, strumenti, settore)  
2. applicano filtri (anni di esperienza, luogo, lingua…)  
3. leggono solo una lista ridotta di CV  

Se il tuo CV è difficile da leggere o non contiene i termini importanti, è meno probabile che venga aperto.

Per costruire un documento adatto, vedi anche la guida su [come creare un CV compatibile con gli ATS per qualsiasi paese](/it/blog/cv-compatibile-ats-per-ogni-paese/).

👉 Con lo [scan ATS gratuito di YourSmartCV](/it/ats-scan/) puoi verificare cosa “vede” un sistema automatico.
`,
      es: `
Al enviar tu CV por internet, muchas empresas lo guardan primero en un sistema ATS, un software de seguimiento de candidatos.

## Qué hace un ATS

Un ATS:

- almacena CV y cartas de presentación  
- extrae información como puestos, fechas y habilidades  
- permite buscar candidatos por palabras clave  
- ayuda a gestionar todo el proceso de selección  

Es, en la práctica, un buscador de candidatos.

## Cómo lo usan los reclutadores

Normalmente:

1. buscan palabras clave del anuncio (puesto, herramientas, sector)  
2. aplican filtros (experiencia, idioma, ubicación…)  
3. revisan solo un grupo reducido de CV  

Si tu CV tiene un diseño complicado o no contiene los términos importantes, el sistema puede leerlo mal y tu perfil casi no aparece.

Para crear un documento más adecuado, consulta la guía sobre [cómo crear un CV compatible con ATS para cualquier país](/es/blog/cv-compatible-ats-para-cualquier-pais/).

👉 Prueba el [escaneo ATS gratuito de YourSmartCV](/es/ats-scan/) para ver cómo interpreta tu CV un sistema real.
`,
    },
  },

  // ---------------------------------------------------------------------------
  // ARTICLE 4 – Common CV mistakes
  // ---------------------------------------------------------------------------
  {
    id: "common-cv-mistakes",
    cluster: "ats-core",
    date: "2025-12-02",
    readTime: "6 min",
    tags: ["Errors", "ATS", "CV"],
    categoryLabel: {
      en: "CV Mistakes",
      fr: "Erreurs de CV",
      de: "Lebenslauf-Fehler",
      it: "Errori nel CV",
      es: "Errores en el CV",
    },
    slug: {
      en: "common-cv-mistakes-that-block-ats-and-recruiters",
      fr: "erreurs-cv-qui-bloquent-ats-et-recruteurs",
      de: "haeufige-fehler-im-lebenslauf-die-ats-blockieren",
      it: "errori-comuni-del-cv-che-bloccano-ats",
      es: "errores-comunes-en-el-cv-que-bloquean-ats",
    },
    h1: {
      en: "Ten Common CV Mistakes That Block ATS And Recruiters",
      fr: "Dix erreurs fréquentes sur le CV qui bloquent ATS et recruteurs",
      de: "Zehn häufige Fehler im Lebenslauf, die ATS und Recruiter blockieren",
      it: "Dieci errori comuni nel CV che bloccano ATS e selezionatori",
      es: "Diez errores comunes en el CV que bloquean ATS y reclutadores",
    },
    metaTitle: {
      en: "Common CV Mistakes That Block ATS And Recruiters",
      fr: "Erreurs CV qui bloquent ATS et recruteurs",
      de: "Häufige Lebenslauf-Fehler, die ATS blockieren",
      it: "Errori comuni del CV che bloccano ATS",
      es: "Errores comunes en el CV que bloquean ATS",
    },
    metaDescription: {
      en: "Avoid common CV mistakes that block ATS and recruiters. Learn what to fix and how a free ATS scan with YourSmartCV can help.",
      fr: "Évitez les erreurs de CV qui bloquent ATS et recruteurs. Découvrez quoi corriger et comment un scan ATS gratuit avec YourSmartCV peut aider.",
      de: "Vermeiden Sie häufige Lebenslauf-Fehler, die ATS und Recruiter blockieren. Erfahren Sie, was Sie verbessern können und wie ein kostenloser ATS-Scan hilft.",
      it: "Evita gli errori più comuni del CV che bloccano ATS e selezionatori. Scopri cosa correggere e come uno scan ATS gratuito con YourSmartCV può aiutarti.",
      es: "Evita los errores más comunes en el CV que bloquean ATS y reclutadores. Aprende qué corregir y cómo puede ayudarte un escaneo ATS gratuito con YourSmartCV.",
    },
    body: {
      en: `
Many candidates have strong skills and experience but still get no interviews. Often the reason is not their profile, it is their CV.

Here are ten common mistakes that make ATS systems and recruiters ignore your application and how to fix them.

> If you do not yet know how ATS works in detail, start with our article on [what an ATS is and how recruiters really use it](/en/blog/what-is-an-ats-and-how-recruiters-use-it/).

## 1. Complex design instead of clear layout

Colourful templates with many columns, graphics and icons look nice on screen but confuse many ATS tools.

✅ Use a simple layout with one main column, clear headings and normal text.

## 2. Important information inside images or icons

If your job titles, dates or skills are inside shapes or images, an ATS may not read them.

✅ Make sure every important detail exists as normal text.

## 3. Missing or vague job titles

A title like “consultant” or “specialist” says almost nothing. Recruiters search by titles that match the job description.

✅ Use titles that reflect the real role, e.g. “marketing manager”, “data scientist”, “product owner”.

## 4. No results, only responsibilities

Long lists of tasks such as “responsible for reporting” do not show your impact.

✅ Turn tasks into results:  
“improved reporting process and reduced manual work by 30%”.

## 5. Wrong file format

Some sites say clearly which format to use. If they ask for PDF, do not send a picture from your phone.

✅ Use PDF in most cases, except when the employer explicitly asks for another format.

## 6. Too long and unfocused

Three or four pages of detail make it hard to see what matters.

✅ Aim for 1–2 pages focused on the last 5–10 years.

## 7. No clear skills section

Recruiters and ATS both look for a skills list.

✅ Create a separate “Key skills” or “Skills” section with grouped bullet points.

## 8. No numbers in experience

Only responsibilities, no impact.

✅ Add metrics: percentages, amounts, volume, time saved.

## 9. Generic profile and no target role

A CV that tries to fit every role fits none.

✅ Make sure your summary and titles clearly point to one direction.

## 10. No ATS testing

You never check how a system reads your CV.

✅ Upload your document to an ATS checker or use YourSmartCV to see:

- what text is extracted  
- which skills are visible  
- where you can improve  

👉 Use the [free ATS scan](/en/ats-scan/) in YourSmartCV and then read how to [create an ATS friendly CV](/en/blog/ats-friendly-cv-any-country/) or [tailor your CV to a job description with AI](/en/blog/tailor-cv-to-job-description-with-ai/).
`,
      fr: `
Beaucoup de candidats ont un bon profil mais peu d’entretiens. La cause vient souvent du CV, pas de l’expérience.

Voici dix erreurs fréquentes qui bloquent à la fois les ATS et les recruteurs, et comment les corriger.

1. **Design trop complexe**

Modèles très colorés, plusieurs colonnes, beaucoup d’icônes… tout cela peut perturber la lecture automatique.

✔ Préférez une mise en page simple, avec une colonne principale et des titres clairs.

2. **Informations importantes dans des images**

Un ATS lit le texte, pas les images.  
✔ Mettez intitulés de poste, dates et compétences en texte normal.

3. **Intitulés de poste vagues**

“Consultant” ou “spécialiste” ne disent pas grand-chose.  
✔ Utilisez des titres proches de ceux des offres : “data scientist”, “marketing manager”, etc.

4. **Aucun résultat chiffré**

Une liste de tâches (responsable de…, chargé de…) ne montre pas votre impact.  
✔ Transformez les tâches en résultats avec des chiffres.

5. **Mauvais format de fichier**

CV pris en photo, documents illisibles…  
✔ En général, envoyez un PDF propre.

6. **CV trop long**

Trois ou quatre pages pleines de détails font perdre le fil.  
✔ Visez 1–2 pages centrées sur les 5–10 dernières années.

7. **Pas de section compétences claire**

✔ Créez une section “Compétences” visible, organisée par thèmes.

8. **Profil trop générique**

✔ Adaptez au moins le titre et le résumé au type de poste visé.

9. **Aucune vérification ATS**

✔ Testez votre document avec un outil comme YourSmartCV pour voir comment il est lu.

Pour comprendre le contexte, commencez par [ce qu’est un ATS et comment les recruteurs l’utilisent vraiment](/fr/blog/quest-ce-quun-ats-et-comment-les-recruteurs-lutilisent/), puis suivez la guide sur [comment créer un CV compatible ATS](/fr/blog/cv-compatible-ats-tous-pays/).

👉 Utilisez le [scan ATS gratuit](/fr/ats-scan/) pour identifier vos erreurs concrètes.
`,
      de: `
Viele Bewerber scheitern nicht an ihren Fähigkeiten, sondern am Lebenslauf selbst. Hier sind zehn typische Fehler.

1. **Zu komplexes Design**  
Bunte Templates mit mehreren Spalten, Icons und Grafiken sehen schön aus, machen es ATS-Systemen aber schwer.

2. **Wichtige Infos in Bildern**  
Jobtitel, Daten oder Skills sollten nie nur als Grafik erscheinen.

3. **Unklare Jobtitel**  
“Consultant” oder “Spezialist” sagen wenig aus – Recruiter suchen nach klaren Rollenbezeichnungen.

4. **Nur Aufgaben, keine Ergebnisse**  
“Verantwortlich für …” reicht nicht. Zahlen und konkrete Erfolge fehlen.

5. **Falsches Dateiformat**  
Unscharfe Fotos vom Handy oder exotische Formate sind ein No-Go. In der Regel ist PDF richtig.

6. **Zu lang und unstrukturiert**  
Seitenlange Aufzählungen erschweren den Überblick. Besser sind 1–2 Seiten mit Fokus.

7. **Keine klare Skill-Sektion**  
Recruiter erwarten einen Abschnitt mit Fähigkeiten, gruppiert nach Themen.

8. **Generisches Profil**  
Ein Lebenslauf für “alles” passt selten zu einer konkreten Rolle.

9. **Keine Zahlen**  
Ohne Kennzahlen ist der tatsächliche Impact schwer zu erkennen.

10. **Kein Test mit einem ATS**  
Wer nie prüft, wie ein System den Lebenslauf liest, verschenkt Chancen.

Mehr technische Hintergründe finden Sie im Beitrag [was ein ATS ist und wie Recruiter es nutzen](/de/blog/was-ist-ein-ats-und-wie-nutzen-recruiter-es/).  

Wenn Sie diese Fehler vermeiden wollen, lesen Sie auch [wie Sie einen ATS freundlichen Lebenslauf für jedes Land erstellen](/de/blog/ats-freundlicher-lebenslauf-fuer-jedes-land/).

👉 Nutzen Sie den [kostenlosen ATS-Scan von YourSmartCV](/de/ats-scan/), um Ihre eigenen Fehler zu entdecken.
`,
      it: `
Molti candidati hanno un buon profilo ma pochi colloqui. Spesso il problema è nel CV.

Errori tipici:

1. **Grafica complessa** – troppi colori, colonne, icone.  
2. **Informazioni chiave in immagini** – titoli, date, competenze non leggibili da un ATS.  
3. **Titoli vaghi** – “consulente”, “specialista” dicono poco.  
4. **Nessun risultato misurabile** – solo elenco di mansioni.  
5. **Formato sbagliato** – foto del CV o file difficili da aprire.  
6. **Documento troppo lungo** – 3–4 pagine piene di dettagli inutili.  
7. **Assenza di una sezione competenze**.  
8. **Profilo generico, senza direzione chiara**.  
9. **Nessun numero** a supporto delle affermazioni.  
10. **Nessun test su un sistema reale**.

Per ridurre questi rischi, crea prima un [CV compatibile con gli ATS](/it/blog/cv-compatibile-ats-per-ogni-paese/), poi usa lo [scan ATS gratuito](/it/ats-scan/) per vedere come un sistema interpreta il tuo documento.

Successivamente puoi [adattare il CV agli annunci con l’IA](/it/blog/adattare-cv-annuncio-di-lavoro-con-ia/) per singole posizioni.
`,
      es: `
Muchos candidatos no fallan por falta de experiencia, sino por la forma de su CV.

Errores frecuentes:

1. **Diseños muy recargados** con varias columnas e iconos.  
2. **Datos clave en imágenes** que el ATS no puede leer.  
3. **Títulos de puesto poco claros** (“consultor”, “especialista”).  
4. **Ningún logro medible**, solo tareas.  
5. **Formato de archivo inadecuado** (fotos, documentos mal escaneados).  
6. **CV muy largo y poco concreto**.  
7. **Falta de una sección de habilidades clara**.  
8. **Perfil genérico**, sin referencia al tipo de puesto.  
9. **Ausencia de cifras** sobre impacto y resultados.  
10. **No probar el CV en un sistema real**.

Si aún no conoces estos sistemas, lee primero [qué es un ATS y cómo lo usan realmente los reclutadores](/es/blog/que-es-un-ats-y-como-lo-usan-los-reclutadores/).

Con YourSmartCV puedes subir tu CV, ver qué partes entiende un ATS y generar una versión más limpia y más alineada con lo que buscan los reclutadores. Luego puedes [adaptar tu CV a ofertas concretas con ayuda de la IA](/es/blog/adaptar-cv-a-una-oferta-con-ia/).

👉 Empieza ahora con el [escaneo ATS gratuito](/es/ats-scan/).
`,
    },
  },

  // ---------------------------------------------------------------------------
  // ARTICLE 5 – Cover letter that matches CV and job
  // ---------------------------------------------------------------------------
  {
    id: "cover-letter-match",
    cluster: "extras",
    date: "2025-12-02",
    readTime: "6 min",
    tags: ["Cover letter", "CV"],
    categoryLabel: {
      en: "Cover Letters",
      fr: "Lettres de motivation",
      de: "Anschreiben",
      it: "Lettere di presentazione",
      es: "Cartas de presentación",
    },
    slug: {
      en: "cover-letter-that-matches-your-cv-and-job-description",
      fr: "lettre-de-motivation-coherente-avec-cv-et-offre",
      de: "anschreiben-das-zu-lebenslauf-und-stelle-passt",
      it: "lettera-di-presentazione-in-linea-con-cv-e-annuncio",
      es: "carta-de-presentacion-que-encaja-con-cv-y-oferta",
    },
    h1: {
      en: "How To Write A Cover Letter That Matches Your CV And The Job Description",
      fr: "Comment écrire une lettre de motivation cohérente avec votre CV et l’offre",
      de: "Wie Sie ein Anschreiben verfassen, das zu Ihrem Lebenslauf und zur Stelle passt",
      it: "Come scrivere una lettera di presentazione in linea con il tuo CV e con l’annuncio",
      es: "Cómo escribir una carta de presentación que encaje con tu CV y con la oferta",
    },
    metaTitle: {
      en: "Write A Cover Letter That Matches Your CV And The Job",
      fr: "Écrire une lettre de motivation cohérente",
      de: "Anschreiben passend zu Lebenslauf und Stelle verfassen",
      it: "Lettera di presentazione in linea con CV e annuncio",
      es: "Carta de presentación en línea con CV y oferta",
    },
    metaDescription: {
      en: "Learn how to write a cover letter that matches your CV and the job description. Clear structure and an easy way to generate one with YourSmartCV.",
      fr: "Apprenez à écrire une lettre de motivation qui correspond à votre CV et à l’offre. Structure simple et génération rapide avec YourSmartCV.",
      de: "Lernen Sie, wie Sie ein Anschreiben schreiben, das zu Ihrem Lebenslauf und zur Stellenausschreibung passt. Mit Unterstützung durch YourSmartCV.",
      it: "Scopri come scrivere una lettera di presentazione in linea con il tuo CV e con l’annuncio. Struttura semplice e generazione rapida con YourSmartCV.",
      es: "Aprende a escribir una carta de presentación que encaje con tu CV y con la oferta. Estructura simple y generación rápida con YourSmartCV.",
    },
    body: {
      en: `
A good CV shows what you have done. A good cover letter explains **why** you are the right person for this specific role.

When the cover letter and CV tell the same story, recruiters trust your application much more.

This guide gives you a simple structure for a cover letter that matches both your CV and the job description – and how YourSmartCV can generate a first version for you.

## 1. Keep one clear goal

A cover letter has one main goal:  
make the recruiter think:

> “This person understands the role and looks like a strong fit. I want to talk to them.”

It does not need to repeat your entire CV or tell your whole life story. Focus on **fit**.

## 2. Use a simple structure

You can follow this structure in any country:

1. **Opening**  
   Say which role you are applying for and where you saw it. Add one sentence that connects you to the company or product if possible.

2. **Paragraph one – fit with the role**  
   Explain in three or four lines why your background fits the main responsibilities. Use one or two strong examples.

3. **Paragraph two – proof with results**  
   Pick one or two achievements from your CV that are most relevant to this role and explain them in a bit more detail. Mention numbers if you can.

4. **Closing**  
   Show enthusiasm and invite the next step. For example:  
   “I look forward to the opportunity to discuss how I can contribute.”

## 3. Make it match the job description

Read the job description and ask:

- what are the three main responsibilities?  
- which skills are clearly important?  
- what type of impact is expected?  

Make sure your cover letter:

- uses similar language for the role and skills  
- highlights one or two projects that prove you can handle those tasks  
- shows you understand the context (startup, corporate, international team…)  

## 4. Make it match your CV

Your cover letter and CV should **never** conflict.

- If you mention a project in the letter, it should appear in the CV experience section.  
- If you claim a skill in the letter, it should also appear in the skills section of the CV.  
- Dates and job titles must match exactly.

Think of the cover letter as a **guided tour** and the CV as the **map**. Both show the same territory.

## 5. Use AI to create the first version and then personalise

With **YourSmartCV** you can:

- upload your CV  
- paste the job description  
- generate an ATS friendly CV and a matching cover letter in the language you choose  

The generated cover letter:

- follows a clear structure  
- reflects the skills and experience from your CV  
- uses keywords from the job description  

You then:

- read it carefully  
- adjust details  
- add your own tone  
- make sure every example is true  

This way you get the speed of AI and the authenticity of your own voice.

👉 To make sure the CV itself is strong, read [how to tailor your CV to a job description with AI](/en/blog/tailor-cv-to-job-description-with-ai/) and [how to create an ATS friendly CV for any country](/en/blog/ats-friendly-cv-any-country/).
`,
      fr: `
Une bonne lettre de motivation montre pourquoi vous êtes adapté à un poste précis. Elle doit **compléter** votre CV, pas le répéter.

## Structure simple

Vous pouvez suivre cette structure :

- **Introduction** – poste visé, où vous avez vu l’annonce, une phrase de lien avec l’entreprise.  
- **Paragraphe 1** – en quoi votre parcours correspond au poste (3–4 lignes).  
- **Paragraphe 2** – un ou deux exemples concrets avec résultats, tirés de votre CV.  
- **Conclusion** – montrer votre intérêt et proposer un échange.

La lettre doit reprendre les mêmes projets, dates et compétences que le CV. Aucune contradiction.

## Lien avec l’offre

Relisez l’annonce et identifiez :

- les missions principales  
- les compétences essentielles  
- le type d’impact attendu  

Reprenez ces éléments dans votre lettre, avec vos propres mots et vos propres exemples.

## Lien avec le CV

Si vous mentionnez un projet dans la lettre, il doit apparaître dans la partie expérience du CV.  
Si vous mettez en avant une compétence, elle doit aussi être visible dans la section compétences.

Pensez à la lettre comme à une “visite guidée” et au CV comme à la carte détaillée.

## Utiliser YourSmartCV

YourSmartCV peut générer une première lettre de motivation à partir de votre CV et de l’offre. Vous obtenez ainsi une base structurée que vous pouvez relire, corriger et personnaliser.

Pour que l’ensemble soit cohérent, commencez par [adapter votre CV à l’offre avec l’IA](/fr/blog/adapter-votre-cv-a-une-offre-avec-ia/) et vérifiez qu’il est bien [compatible ATS](/fr/blog/cv-compatible-ats-tous-pays/).
`,
      de: `
Das Anschreiben beantwortet die Frage, warum Sie **genau für diese Stelle** geeignet sind. Es ergänzt den Lebenslauf.

## Sinnvolle Struktur

- Einstieg mit Stellenbezeichnung und Bezug zum Unternehmen  
- kurzer Abschnitt zur eigenen Passung  
- ein oder zwei Beispiele mit Ergebnissen aus dem Lebenslauf  
- freundlicher Abschluss mit Hinweis auf Gesprächswunsch  

Wichtig ist, dass die Angaben mit dem Lebenslauf übereinstimmen. Projekte, Daten und Rollen sollten in beiden Dokumenten auftauchen.

## Bezug zur Stellenausschreibung

Greifen Sie Begriffe aus der Anzeige auf: zentrale Aufgaben, Skills, Erwartungen. Zeigen Sie, dass Sie verstanden haben, worum es in der Rolle geht.

## Konsistenz mit dem Lebenslauf

Wenn ein Projekt im Anschreiben wichtig ist, sollte es auch im Lebenslauf erscheinen.  
Wenn Sie eine Fähigkeit betonen, muss sie in der Skill-Sektion wiederzufinden sein.

YourSmartCV erstellt auf Basis Ihres Lebenslaufs und der Stellenanzeige ein erstes Anschreiben in der gewünschten Sprache. Sie passen Formulierungen und Ton anschließend an.

👉 Noch wirksamer wird das Ganze, wenn Ihr CV bereits [an die Stelle angepasst](/de/blog/lebenslauf-an-stellenausschreibung-mit-ki-anpassen/) und [ATS freundlich aufgebaut](/de/blog/ats-freundlicher-lebenslauf-fuer-jedes-land/) ist.
`,
      it: `
Una buona lettera di presentazione spiega perché sei adatto **proprio a quella posizione** e completa il CV.

## Struttura consigliata

1. Introduzione – ruolo per cui ti candidi e dove hai visto l’annuncio.  
2. Paragrafo sulla tua aderenza al ruolo – 3–4 frasi su esperienza e competenze chiave.  
3. Paragrafo con risultati – uno o due esempi concreti tratti dal CV, con numeri.  
4. Chiusura – mostra interesse e proponi un colloquio.

La lettera deve essere coerente con il CV: stessi progetti, stesse date, stessi ruoli.

YourSmartCV genera una lettera di presentazione a partire dal tuo CV e dall’annuncio, in diverse lingue. Tu la rileggi, correggi e adatti il tono.

Per un allineamento completo, leggi come [adattare il tuo CV a un annuncio con l’IA](/it/blog/adattare-cv-annuncio-di-lavoro-con-ia/) e come [creare un CV compatibile con gli ATS](/it/blog/cv-compatibile-ats-per-ogni-paese/).
`,
      es: `
Una buena carta de presentación responde a la pregunta: **por qué tú para este puesto concreto**. No repite todo el CV, lo complementa.

## Estructura recomendada

- introducción con el puesto y una frase sobre la empresa  
- párrafo que conecta tu experiencia con las tareas del puesto  
- uno o dos logros del CV explicados con más detalle  
- cierre corto mostrando interés en una entrevista  

La información de la carta debe coincidir con la del CV: mismos proyectos, fechas y cargos.

YourSmartCV genera una carta de presentación a partir de tu CV y de la descripción del puesto, en distintos idiomas. Después tú la revisas y ajustas el tono.

Para que todo encaje, es útil [adaptar tu CV a la oferta con ayuda de la IA](/es/blog/adaptar-cv-a-una-oferta-con-ia/) y asegurarte antes de que sea [compatible con ATS](/es/blog/cv-compatible-ats-para-cualquier-pais/).
`,
    },
  },

  // ---------------------------------------------------------------------------
  // ARTICLE 6 – Use AI safely and keep your own voice
  // ---------------------------------------------------------------------------
  {
    id: "use-ai-safely",
    cluster: "extras",
    date: "2025-12-02",
    readTime: "6 min",
    tags: ["AI", "Safety", "CV"],
    categoryLabel: {
      en: "AI & CV",
      fr: "IA & CV",
      de: "KI & Lebenslauf",
      it: "IA & CV",
      es: "IA & CV",
    },
    slug: {
      en: "use-ai-safely-for-your-cv-and-keep-your-own-voice",
      fr: "utiliser-ia-pour-votre-cv-sans-perdre-votre-voix",
      de: "ki-sicher-fuer-lebenslauf-nutzen-und-eigene-stimme-behalten",
      it: "usare-ia-per-il-cv-mantenendo-la-propria-voce",
      es: "usar-ia-para-el-cv-manteniendo-tu-propia-voz",
    },
    h1: {
      en: "How To Use AI Safely For Your CV Without Losing Your Own Voice",
      fr: "Comment utiliser l’IA pour votre CV sans perdre votre propre voix",
      de: "Wie Sie KI sicher für Ihren Lebenslauf nutzen und trotzdem Ihre eigene Stimme behalten",
      it: "Come usare l’IA per il tuo CV senza perdere la tua voce",
      es: "Cómo usar la IA para tu CV sin perder tu propia voz",
    },
    metaTitle: {
      en: "Use AI Safely For Your CV And Keep Your Own Voice",
      fr: "Utiliser l’IA pour votre CV sans perdre votre voix",
      de: "KI sicher für den Lebenslauf nutzen und eigene Stimme behalten",
      it: "Usare l’IA per il CV mantenendo la propria voce",
      es: "Usar la IA para el CV manteniendo tu propia voz",
    },
    metaDescription: {
      en: "Learn how to use AI safely for your CV, avoid risks, and keep your own voice. YourSmartCV helps you stay in control.",
      fr: "Apprenez à utiliser l’IA pour votre CV de façon sûre, éviter les risques et garder votre propre voix. YourSmartCV vous aide à garder le contrôle.",
      de: "Erfahren Sie, wie Sie KI sicher für Ihren Lebenslauf nutzen, Risiken vermeiden und Ihre eigene Stimme behalten. YourSmartCV hilft Ihnen dabei.",
      it: "Scopri come usare l’IA in modo sicuro per il tuo CV, evitare rischi e mantenere la tua voce. YourSmartCV ti aiuta a restare in controllo.",
      es: "Aprende cómo usar la IA de forma segura para tu CV, evitar riesgos y mantener tu propia voz. YourSmartCV te ayuda a tener el control.",
    },
    body: {
      en: `
AI tools can help you write and improve your CV much faster. They can structure your experience, suggest strong verbs and fix grammar in several languages.

At the same time, many candidates worry that AI will make all CVs look the same or that they might share too much personal data. Here is how to use AI for your CV in a safe and smart way.

## 1. Let AI assist, do not let it invent your story

AI is powerful at rewriting, summarising and organising information. It is not a replacement for your real experience.

Use AI to:

- turn rough notes into clean bullets  
- improve grammar and clarity  
- adapt your CV to a new role or language  

Do **not** use AI to:

- invent jobs or skills you never had  
- add fake numbers or projects  
- create a profile that you cannot defend in an interview  

Recruiters can feel when a CV does not match how a person speaks.

## 2. Always review and edit the result

Never send a CV that you have not fully read. After AI generates text, you should:

- check every line for truth and accuracy  
- correct dates, job titles and company names  
- adjust wording so it sounds natural for you  
- remove anything that feels strange or too generic  

Treat the AI output as a draft, not a final product.

## 3. Be careful with personal and sensitive data

When you upload a CV to any tool, you share information such as name, contact details, work history and sometimes more.

Use services that:

- explain clearly how they store and process data  
- do not sell personal data  
- let you delete your documents  

YourSmartCV works with CVs to generate new documents in a privacy-conscious way, but **you** decide which CV you upload and when you remove it.

## 4. Keep your own voice

If every CV sounds like the same generic AI text, recruiters lose interest. You can keep your voice by:

- adding specific details from your real projects  
- using examples from your industry or country  
- letting some of your natural style stay in the text  

AI can clean your sentences, but you can still keep one or two phrases that feel like you.

## 5. Use tools that are focused on CVs

Generic AI chat tools can help, but they do not know your structure, design and export needs. A specialised tool like YourSmartCV:

- understands typical CV sections and recruiter expectations  
- focuses on ATS friendly formatting  
- supports several languages for global applications  
- lets you generate both CV and cover letter in a consistent style  

You stay in control of the final result while saving a lot of time.

👉 Try YourSmartCV to combine safe AI support with [ATS friendly CVs](/en/blog/ats-friendly-cv-any-country/) and [tailored applications](/en/blog/tailor-cv-to-job-description-with-ai/).
`,
      fr: `
L’IA peut vous aider à rédiger un CV plus clair, plus structuré et sans fautes, dans plusieurs langues. Mais elle ne doit pas remplacer votre histoire.

### 1. Laisser l’IA aider, pas inventer

Utilisez-la pour organiser, reformuler, corriger.  
Ne l’utilisez pas pour inventer des postes ou des chiffres.

### 2. Relire systématiquement

Ne jamais envoyer un texte généré sans relecture :

- vérifiez chaque phrase  
- corrigez les détails factuels  
- ajustez le ton pour qu’il vous ressemble  

### 3. Attention aux données personnelles

Choisissez des outils qui expliquent comment les données sont stockées et qui vous permettent de les supprimer.

### 4. Garder votre voix

Ajoutez des exemples concrets, des expressions qui vous ressemblent.  
L’IA nettoie, mais vous gardez le style.

YourSmartCV est conçu pour vous assister, pas pour parler à votre place. Vous importez votre CV, l’outil propose une version optimisée, puis vous gardez la main sur les corrections et le style final.

> Un bon exemple d’usage est d’[adapter votre CV à une offre avec l’IA](/fr/blog/adapter-votre-cv-a-une-offre-avec-ia/) ou de [générer une lettre de motivation cohérente avec votre CV](/fr/blog/lettre-de-motivation-coherente-avec-cv-et-offre/).

👉 Découvrez comment l’IA et l’ATS peuvent travailler ensemble grâce à [YourSmartCV](/fr/ats-scan/).
`,
      de: `
KI kann Formulierungen verbessern, Fehler korrigieren und Inhalte strukturieren. Dennoch sollte der Lebenslauf Ihre Person widerspiegeln.

### 1. KI als Unterstützung nutzen

Gut geeignet ist sie für:

- Strukturierung von Stichpunkten  
- sprachliche Optimierung  
- Anpassung an andere Sprachen oder Rollen  

Nicht geeignet ist sie zum Erfinden von Stationen oder Erfolgen.

### 2. Ergebnis immer prüfen

Lesen Sie jede Zeile und fragen Sie sich: stimmt das, klingt das nach mir?

### 3. Sensible Daten schützen

Nutzen Sie Dienste, die transparent mit Speicherung und Löschung von Daten umgehen.

### 4. Eigene Stimme behalten

Ergänzen Sie konkrete Beispiele, behalten Sie ein paar typische Formulierungen bei. So bleibt der Text glaubwürdig.

YourSmartCV unterstützt Sie bei Format und Struktur und berücksichtigt typische Anforderungen von Recruitern und ATS-Systemen. Die Kontrolle über Inhalt und Stimme bleibt bei Ihnen.

> Sinnvoll ist KI etwa beim [Anpassen des Lebenslaufs an eine Stellenausschreibung](/de/blog/lebenslauf-an-stellenausschreibung-mit-ki-anpassen/) oder beim Erstellen eines [Anschreibens, das zu Lebenslauf und Stelle passt](/de/blog/anschreiben-das-zu-lebenslauf-und-stelle-passt/).

👉 Nutzen Sie [YourSmartCV](/de/ats-scan/), um KI sicher in Ihren Bewerbungsprozess einzubauen.
`,
      it: `
L’IA può aiutarti a ordinare le idee, correggere errori e adattare il CV a ruoli e lingue diverse. Ma il contenuto deve restare tuo.

### Consigli

- usa l’IA per generare bozze, non per inventare esperienze  
- controlla sempre il risultato  
- aggiungi dettagli reali dei tuoi progetti  
- fai attenzione ai dati personali che condividi  

YourSmartCV ti permette di combinare velocità e controllo: generi una versione proposta e poi la personalizzi.

> Un buon uso è [adattare il tuo CV a un annuncio con l’IA](/it/blog/adattare-cv-annuncio-di-lavoro-con-ia/) o [creare una lettera di presentazione in linea con il CV e con l’annuncio](/it/blog/lettera-di-presentazione-in-linea-con-cv-e-annuncio/).

👉 Combina sicurezza e velocità con [YourSmartCV](/it/ats-scan/).
`,
      es: `
La IA puede ayudarte a ordenar ideas, corregir errores y adaptar tu CV a distintos puestos y lenguas. Pero el contenido debe seguir siendo tuyo.

### Recomendaciones

- usa la IA para generar borradores, no para inventar experiencia  
- revisa siempre el resultado  
- añade detalles reales de tus proyectos  
- cuida la información personal que compartes  

Con YourSmartCV puedes combinar la velocidad de la IA con tu propia revisión y así obtener un CV claro, compatible con ATS y fiel a tu forma de presentarte.

> Un buen ejemplo es [adaptar tu CV a una oferta con ayuda de la IA](/es/blog/adaptar-cv-a-una-oferta-con-ia/) y [escribir una carta de presentación que encaje con tu CV y con la oferta](/es/blog/carta-de-presentacion-que-encaja-con-cv-y-oferta/).

👉 Empieza con [YourSmartCV](/es/ats-scan/) para usar la IA de forma segura en tus candidaturas.
`,
    },
  },
];

// ---- Helpers for routing / SEO ----
export const SUPPORTED_LANGS = ["en", "fr", "de", "it", "es"];

export function findPostBySlug(language, slug) {
  const lang = SUPPORTED_LANGS.includes(language) ? language : "en";
  return (
    BLOG_POSTS.find((post) => post.slug && post.slug[lang] === slug) || null
  );
}
